import type { Ft3dAoePoint } from './ft3d-aoe-placement-contract';
import { FT_SELF_RANGE_ID } from './aoe-caster-exclusion';
import { resolveFormulaArithmetic } from '../janus-engine/compile/formula-variable-resolution';

export interface TeleportPlacementRequest {
    casterId: string;
    encounterId: string;
    abilityId: string;
    name: string;
    /** null = the range carries a formula tag the browser cannot resolve. */
    rangeFt: number | null;
    sizeFt: number;
    offerId?: string;
    /** The acting creature when it is not the placed one (Ft3dAoePlacementIntent.controllerId). */
    controllerId?: string;
    cast: (point: Ft3dAoePoint) => void;
}

declare global {
    interface Window {
        ftStartTeleportPlacement?: (request: TeleportPlacementRequest) => void;
        ftPlanTeleportOfferLanding?: (encounterId: string, actorId: string, point: Ft3dAoePoint, rangeFt: number)
            => Promise<import('../janus-engine/statuses/gm-boundary').GmBoundaryVerdict>;
        ftTeleportOfferLandingCommitted?: (encounterId: string, verdict: import('../janus-engine/statuses/gm-boundary').GmBoundaryVerdict, offerId?: string, actorId?: string) => void | Promise<void>;
        ftOfferFortunate?: (encounterId: string, actorId: string, reactorIds: readonly string[]) => void;
    }
}

// Browser preparation shapes the placement surface; the browser settlement verdict owns admission.

type LayerReader = (key: string) => unknown;

const OTHER_SPATIAL_CONTRACT_LAYERS = ['aoe_type', 'teleport', 'max_targets',
    'caster_moves_towards_target_in_ft', 'caster_moves_to_end_point_of_aoe_effect_after_cast'];
const OTHER_TIMING_CONTRACT_TYPES = ['passive_ability', 'attack_ability', 'opportunity_attack_ability'];

const layerText = (value: unknown): string => value == null ? '' : String(Array.isArray(value) ? value[0] ?? '' : value);

/** PHP (float) cast: the leading numeric prefix of a string, else 0. */
function phpFloat(value: unknown): number {
    if (typeof value === 'number') return value;
    const match = /^[ \t\n\r\v\f]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/.exec(String(value ?? ''));
    return match ? Number(match[0].trim()) : 0;
}

/** Twin of ft_direct_teleport_ability() (teleport_destination.php). */
export function isDirectTeleportAbility(layer: LayerReader, itemType: string): boolean {
    const raw = layerText(layer('change_teleport_remaining'));
    if (raw === '' || /^\s*(?:-|\{?0\}?\s*$)/.test(raw)) return false;
    if (OTHER_TIMING_CONTRACT_TYPES.includes(itemType)) return false;
    if (OTHER_SPATIAL_CONTRACT_LAYERS.some(key => layer(key) != null)) return false;
    return layerText(layer('range')).toUpperCase() === FT_SELF_RANGE_ID;
}

/** Twin of ft_teleport_ability_distance() for tag-free values (every catalog value at 2026-09-10 is "+N").
 * A tagged value returns null: the placement then draws no range limit and the commit judges it. */
export function teleportAbilityDistanceFt(layer: LayerReader): number | null {
    const raw = layerText(layer('change_teleport_remaining'));
    if (raw.includes('[') || raw === 'v_lost') return null;
    return Math.max(0, phpFloat(resolveFormulaArithmetic(raw)));
}

/** Twin of ft_ground_occupancy_base_size(): the size tier's cubic dimension, Medium (5 ft) when unreadable. */
export function groundOccupancyBaseSizeFt(sizeId: unknown, sizes: readonly { item_id: string; base_size: number }[]): number {
    const id = layerText(sizeId).toUpperCase();
    const width = id ? Number(sizes.find(size => size.item_id.toUpperCase() === id)?.base_size ?? 0) : 0;
    return width > 0 ? width : 5;
}

/** Browser composition over the page's cached ability definition and caster row. */
export function browserTeleportPreparation(ability: unknown, caster: unknown): { eligible: boolean; rangeFt: number | null; sizeFt: number } {
    const layer: LayerReader = key => ftLayerValue(ability, key);
    const itemType = String(ftItemsCol(ability, 'item_type') ?? '').toLowerCase();
    if (!ability || !isDirectTeleportAbility(layer, itemType)) return { eligible: false, rangeFt: null, sizeFt: 5 };
    const sizeId = caster ? ftEffectiveSizeId(caster) ?? ftLayerValue(caster, 'size') : null;
    return { eligible: true, rangeFt: teleportAbilityDistanceFt(layer), sizeFt: groundOccupancyBaseSizeFt(sizeId, SIZE_TYPES) };
}
